/** @jsxImportSource @emotion/react */
import { Button, Grid } from '@mui/material';
import React, { Fragment, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { StatisticField, statisticLabels } from '../../constants';
import { DataFiltersModel, FullStatisticModel } from '../../models';
import { selectUser } from '../../store/reducers';
import { isUserPublisher } from '../../utils';
import { numberFormat, toPriceFormat } from '../../utils/cell-formatters';
import { MainLoader } from '../main-loader';
import { useSummaryStatisticStyles } from './SummaryStatistic.styles';
import { SummaryStatisticItem } from './SummaryStatisticItem';

interface SummaryStatisticProps {
  onlyRateDataStatistic?: boolean;
  allowFullStatisticWithRatesData?: boolean;
  filters: DataFiltersModel;
  data?: FullStatisticModel;
  loading?: boolean;
  onFetch?: () => void;
}

export function SummaryStatistic({
  filters,
  data,
  loading,
  onFetch,
  onlyRateDataStatistic,
  allowFullStatisticWithRatesData,
}: SummaryStatisticProps): ReactElement {
  const style = useSummaryStatisticStyles();

  const user = useSelector(selectUser);

  const btnContent = () => (
    <Grid item xs={12} className="align-right">
      <Button
        className="fetch-summ-stat-button"
        color="primary"
        type="button"
        variant="contained"
        disabled={loading}
        onClick={onFetch}
      >
        Fetch summary statistic
      </Button>
    </Grid>
  );

  // @ts-ignore
  const dataContent = () => (
    <Fragment>
      <SummaryStatisticItem
        title={statisticLabels[StatisticField.StatCnt]}
        value={toPriceFormat(data[StatisticField.StatCnt], 0)}
      />

      {filters.oneDay && !onlyRateDataStatistic ? (
        <SummaryStatisticItem
          title={statisticLabels[StatisticField.StatUniqCnt]}
          value={toPriceFormat(data[StatisticField.StatUniqCnt], 0)}
        />
      ) : null}

      {onlyRateDataStatistic ? (
        <SummaryStatisticItem
          title={`${isUserPublisher(user) ? 'PR' : 'R'}${
            statisticLabels[StatisticField.Ecpm]
          }`}
          value={numberFormat(data[StatisticField.RateEcpm])}
        />
      ) : (
        <SummaryStatisticItem
          title={statisticLabels[StatisticField.Ecpm]}
          value={numberFormat(data[StatisticField.Ecpm])}
          subTitle={statisticLabels[StatisticField.RateEcpm]}
          subValue={numberFormat(data[StatisticField.RateEcpm])}
          subSubTitle={
            allowFullStatisticWithRatesData
              ? `PR${statisticLabels[StatisticField.Ecpm]}`
              : undefined
          }
          subSubValue={
            allowFullStatisticWithRatesData
              ? numberFormat(data[StatisticField.BaseRateEcpm])
              : undefined
          }
        />
      )}
      {onlyRateDataStatistic ? (
        <SummaryStatisticItem
          title={`${isUserPublisher(user) ? 'PR' : 'R'}${
            statisticLabels[StatisticField.StatSum]
          }`}
          value={numberFormat(data[StatisticField.StatRateSum])}
        />
      ) : (
        <SummaryStatisticItem
          title={statisticLabels[StatisticField.StatSum]}
          value={numberFormat(data[StatisticField.StatSum])}
          subTitle={statisticLabels[StatisticField.StatRateSum]}
          subValue={numberFormat(data[StatisticField.StatRateSum])}
          subSubTitle={
            allowFullStatisticWithRatesData
              ? `PR${statisticLabels[StatisticField.StatSum]}`
              : undefined
          }
          subSubValue={
            allowFullStatisticWithRatesData
              ? numberFormat(data[StatisticField.BaseStatRateSum])
              : undefined
          }
        />
      )}
    </Fragment>
  );

  return (
    <div css={style}>
      {loading && <MainLoader fillContainer />}
      <Grid container spacing={2} alignContent="flex-end">
        {data ? dataContent() : btnContent()}
      </Grid>
    </div>
  );
}
